import { useContext, useMemo } from "react"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import useSeasonRecoveryLessons from "../lesson/useSeasonRecoveryLessons"
import { absencesByCourse, getSeasonBounds, openSpotsByCourse } from "../utils/substitutes.utils"

// quanti giorni avanti si guardano i posti da riempire
export const OPEN_SPOTS_DAYS = 7

// Le assenze da recuperare non stanno in un mese: una di settembre può
// restare aperta fino a primavera. Qui si guarda la stagione intera, a
// prescindere dal periodo scelto nel profilo, e si leggono solo le lezioni
// che contano per i recuperi (`tracksRecovery`).
//
// Del maestro che si sta guardando: le sue lezioni, da titolare o da
// assistente; "Tutti" le vuole tutte. Divise per corso, perché un CAD
// saltato si recupera solo in un CAD e un SAT in un SAT. Dalle stesse
// lezioni, i posti da riempire dei prossimi giorni.
const useSeasonAbsences = () => {
	const { selectedSchool } = useContext(schoolContext)
	const { displayedCoachID } = useContext(profileContext)

	// la stagione di oggi: si ricalcola solo a cavallo di settembre, e un
	// profilo aperto quel giorno lo si riapre
	const season = useMemo(() => getSeasonBounds(new Date()), [])

	const lessons = useSeasonRecoveryLessons(selectedSchool, season.start)

	const coachLessons = useMemo(
		() =>
			(lessons ?? []).filter(
				lesson =>
					displayedCoachID === "Tutti" ||
					lesson.coachID === displayedCoachID ||
					(lesson.assistantCoachIDs || []).includes(displayedCoachID),
			),
		[lessons, displayedCoachID],
	)

	const byCourse = useMemo(() => absencesByCourse(coachLessons), [coachLessons])

	// I posti da riempire dei prossimi giorni: le stesse lezioni, quelle che
	// devono ancora cominciare. La stagione le ha già tutte, anche quelle
	// future: nessuna lettura in più. "Adesso" è quando arriva la lettura;
	// una lezione che comincia mentre la scheda è aperta resta finché non
	// cambia qualcosa, ed è giusto così: il sostituto si può ancora mettere.
	const spotsByCourse = useMemo(() => {
		const now = new Date()
		const until = new Date(now.getTime() + OPEN_SPOTS_DAYS * 24 * 60 * 60 * 1000)
		return openSpotsByCourse(coachLessons, now, until)
	}, [coachLessons])

	return { byCourse, spotsByCourse, season, isLoaded: lessons !== null }
}

export default useSeasonAbsences
